// Mock data & calculation helpers for EMI Calculator

export const defaultValues = {
  loanAmount: 1000000,
  interestRate: 8.5,
  loanTenure: 5,
  tenureType: 'Yr',
  moratoriumEnabled: false,
  moratoriumMonths: 3,
  moratoriumType: 'full', // 'full' (interest capitalises) | 'interest' (pay only interest)
  interestMethod: 'reducing', // 'reducing' | 'flat'
  chartType: 'donut', // 'donut' | 'bar' | 'line'
  advanced: {
    creditDaysEnabled: false,
    creditDays: 30,
    discountingRate: null, // null => falls back to selected ROI
    subventionEnabled: false,
    subventionAmount: 100000,
  },
};

export const ranges = {
  loanAmount: { min: 100000, max: 100000000, step: 10000 },
  interestRate: { min: 1, max: 30, step: 0.1 },
  loanTenureYr: { min: 1, max: 30, step: 1 },
  loanTenureMo: { min: 1, max: 360, step: 1 },
  moratoriumMonths: { min: 1, max: 24, step: 1 },
};

export const faqs = [
  {
    q: 'What is an EMI?',
    a: 'EMI stands for Equated Monthly Installment. It is the fixed amount you pay to your lender every month on a specified date until your loan is fully repaid. Each EMI consists of two components — principal repayment and interest on the outstanding loan.',
  },
  {
    q: 'How is EMI calculated?',
    a: 'EMI is calculated using the formula: EMI = [P x R x (1+R)^N] / [(1+R)^N - 1], where P is the principal loan amount, R is the monthly interest rate, and N is the loan tenure in months.',
  },
  {
    q: 'What is a moratorium period?',
    a: 'A moratorium is a pause on your regular EMI payments, typically given at the start of a loan. Interest usually keeps accruing during this period and either gets added to your outstanding principal (increasing future EMIs) or is paid separately as interest-only installments.',
  },
  {
    q: 'How does prepayment help?',
    a: 'Prepaying part of your loan directly reduces the outstanding principal, which cuts down the total interest you pay. You can either keep the EMI the same and finish the loan faster, or shorten the EMI while keeping the tenure the same.',
  },
  {
    q: 'Is it better to choose a longer or shorter loan tenure?',
    a: 'A shorter tenure means higher EMIs but significantly lower total interest outgo. A longer tenure eases monthly cash-flow but increases total interest. Choose based on your monthly budget and long-term financial goals.',
  },
  {
    q: 'Does the EMI calculator include processing fees or GST?',
    a: 'No. The calculator gives you the pure principal-plus-interest breakup. Processing fees, insurance, GST and other lender charges are separate and are not part of the monthly EMI displayed here.',
  },
  {
    q: 'What are credit days and how do they affect my EMI?',
    a: 'Credit days are a grace period offered by the builder, dealer or manufacturer during which they absorb the financing cost. The interest for that grace period is discounted from your effective principal, so both your EMI and your effective interest rate come down.',
  },
  {
    q: 'What is interest subvention?',
    a: 'Interest subvention is an upfront reduction in the loan burden — typically offered by builders, dealers or manufacturers. The subvention amount is deducted from the effective principal on which EMIs are calculated, so both EMI and total interest come down.',
  },
  {
    q: 'Can I combine credit days with interest subvention?',
    a: 'Yes. Each benefit reduces the effective principal independently, and their impact stacks. You can enable either one alone, or both together — the calculator will show the revised EMI, revised amortization schedule and effective ROI for whichever combination you pick.',
  },
];

export const otherCalculators = [
  { name: 'SIP Calculator', desc: 'Plan your monthly investments and see the corpus you can build over time.' },
  { name: 'Lumpsum Calculator', desc: 'Estimate the future value of a one-time investment based on expected returns.' },
  { name: 'PPF Calculator', desc: 'Project the maturity value of your Public Provident Fund contributions.' },
  { name: 'FD Calculator', desc: 'Calculate the interest and maturity amount of your fixed deposits.' },
  { name: 'RD Calculator', desc: 'Compute returns on your recurring deposits over any tenure.' },
  { name: 'NPS Calculator', desc: 'Estimate your retirement corpus and monthly pension via NPS.' },
  { name: 'HRA Calculator', desc: 'Find the House Rent Allowance you can claim as a tax exemption.' },
  { name: 'Step Up SIP Calculator', desc: 'See how increasing your SIP annually can supercharge your wealth.' },
];

// --- Core EMI math ----------------------------------------------------------

function emiFormula(P, monthlyRate, n) {
  if (P <= 0 || n <= 0) return 0;
  if (monthlyRate === 0) return P / n;
  const pow = Math.pow(1 + monthlyRate, n);
  return (P * monthlyRate * pow) / (pow - 1);
}

export function calculateEmi(principal, annualRate, tenureMonths) {
  const emi = emiFormula(Number(principal) || 0, (Number(annualRate) || 0) / 12 / 100, Number(tenureMonths) || 0);
  const totalPayment = emi * (Number(tenureMonths) || 0);
  return {
    emi: Math.round(emi),
    totalInterest: Math.round(totalPayment - (Number(principal) || 0)),
    totalPayment: Math.round(totalPayment),
  };
}

/**
 * Full simulation supporting moratorium & prepayments, plus advanced options
 * that DISCOUNT the effective principal (credit days & subvention).
 */
export function simulateLoan({
  principal,
  annualRate,
  tenureMonths,
  moratorium = { enabled: false, months: 0, type: 'full' },
  prepayments = [],
  extraEmi = 0,
  interestMethod = 'reducing',
  advanced = {},
}) {
  const originalPrincipal = Math.max(0, Number(principal) || 0);

  // ---- Advanced pre-processing (both reduce effective principal) ---------
  let effectivePrincipal = originalPrincipal;
  let creditDaysDiscount = 0;
  let subventionApplied = 0;

  if (advanced.creditDaysEnabled && Number(advanced.creditDays) > 0) {
    const days = Number(advanced.creditDays);
    // default discounting rate = selected ROI when unset / falsy
    const dRate = (advanced.discountingRate == null || advanced.discountingRate === '')
      ? Number(annualRate) || 0
      : Number(advanced.discountingRate);
    // Grace period offered by seller — dealer absorbs interest for `days`,
    // so the equivalent discount is subtracted from the effective principal.
    creditDaysDiscount = originalPrincipal * (dRate / 100) * (days / 365);
    effectivePrincipal = Math.max(0, effectivePrincipal - creditDaysDiscount);
  }
  if (advanced.subventionEnabled && Number(advanced.subventionAmount) > 0) {
    subventionApplied = Math.min(Number(advanced.subventionAmount), effectivePrincipal);
    effectivePrincipal = Math.max(0, effectivePrincipal - subventionApplied);
  }

  const sim = (interestMethod === 'flat' ? simulateFlat : simulateReducing)({
    principal: effectivePrincipal,
    annualRate,
    tenureMonths,
    moratorium,
    prepayments,
    extraEmi,
  });

  // Compute the effective annual rate the user is really paying on the
  // ORIGINAL loan amount by solving: emi = emiFormula(P_orig, r/12/100, N)
  const effRate = solveEffectiveAnnualRate(originalPrincipal, sim.summary.emi, tenureMonths);

  sim.summary.principal = Math.round(originalPrincipal);
  sim.summary.effectivePrincipal = Math.round(effectivePrincipal);
  sim.summary.creditDaysDiscount = Math.round(creditDaysDiscount);
  // Keep legacy key for anywhere still referencing it, but it's now the discount.
  sim.summary.creditInterest = -Math.round(creditDaysDiscount);
  sim.summary.subventionApplied = Math.round(subventionApplied);
  sim.summary.effectiveAnnualRate = effRate;
  return sim;
}

function simulateReducing({ principal, annualRate, tenureMonths, moratorium, prepayments, extraEmi }) {
  const P = Math.max(0, Number(principal) || 0);
  const N = Math.max(0, Math.floor(Number(tenureMonths) || 0));
  const r = (Number(annualRate) || 0) / 12 / 100;
  const extra = Math.max(0, Number(extraEmi) || 0);

  const schedule = [];
  let balance = P;
  let totalInterest = 0;
  let totalPayment = 0;
  let moratoriumInterestCapitalised = 0;
  let moratoriumInterestPaid = 0;

  const morEnabled = moratorium.enabled && moratorium.months > 0;
  const morMonths = morEnabled ? Math.floor(moratorium.months) : 0;
  const morType = moratorium.type || 'full';

  // 1. Moratorium phase
  for (let m = 1; m <= morMonths; m++) {
    const interest = balance * r;
    if (morType === 'full') {
      balance += interest;
      moratoriumInterestCapitalised += interest;
      schedule.push({ month: m, phase: 'Moratorium', emi: 0, principalPaid: 0, interestPaid: 0, moratoriumInterest: interest, prepayment: 0, balance });
    } else {
      totalInterest += interest;
      totalPayment += interest;
      moratoriumInterestPaid += interest;
      schedule.push({ month: m, phase: 'Moratorium (Int)', emi: interest, principalPaid: 0, interestPaid: interest, prepayment: 0, balance });
    }
  }

  const remaining = Math.max(1, N - morMonths);
  let emi = emiFormula(balance, r, remaining);

  const prepayMap = new Map();
  prepayments.forEach((p) => {
    if (p && p.month > 0 && p.amount > 0) {
      prepayMap.set(Number(p.month), (prepayMap.get(Number(p.month)) || 0) + Number(p.amount));
    }
  });

  let month = morMonths;
  const hardCap = morMonths + remaining + 600;
  while (balance > 0.5 && month < hardCap) {
    month += 1;
    const interest = balance * r;
    let principalPaid = emi - interest;
    if (principalPaid < 0) principalPaid = 0;
    let extraPay = Math.min(extra, Math.max(0, balance - principalPaid));
    let payment = emi;

    if (principalPaid + extraPay >= balance) {
      principalPaid = Math.min(principalPaid, balance);
      extraPay = Math.max(0, balance - principalPaid);
      payment = principalPaid + interest;
    }
    balance -= principalPaid + extraPay;

    let prepay = prepayMap.get(month) || 0;
    if (prepay > balance) prepay = balance;
    balance -= prepay;

    totalInterest += interest;
    totalPayment += payment + extraPay + prepay;

    schedule.push({
      month,
      phase: 'EMI',
      emi: payment,
      principalPaid,
      interestPaid: interest,
      prepayment: extraPay + prepay,
      balance: Math.max(0, balance),
    });
    if (balance <= 0.5) break;
  }

  return {
    schedule,
    summary: {
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalPayment: Math.round(totalPayment),
      monthsTaken: month,
      moratoriumInterestCapitalised: Math.round(moratoriumInterestCapitalised),
      moratoriumInterestPaid: Math.round(moratoriumInterestPaid),
      postMoratoriumPrincipal: Math.round(P + moratoriumInterestCapitalised),
    },
  };
}

// Bisection: find annual rate (percent) such that emiFormula(P, r/12/100, N) == targetEmi
function solveEffectiveAnnualRate(principal, targetEmi, tenureMonths) {
  const P = Math.max(0, Number(principal) || 0);
  const N = Math.max(1, Math.floor(Number(tenureMonths) || 0));
  const t = Math.max(0, Number(targetEmi) || 0);
  if (P === 0 || t === 0) return 0;
  // If EMI * N <= P → 0 interest (or negative, treat as 0)
  if (t * N <= P + 0.5) return 0;

  let lo = 0;
  let hi = 100;
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    const emi = emiFormula(P, mid / 12 / 100, N);
    if (emi > t) hi = mid;
    else lo = mid;
    if (hi - lo < 1e-4) break;
  }
  return Number(((lo + hi) / 2).toFixed(3));
}

// --- Flat-rate simulator ----------------------------------------------------
function simulateFlat({ principal, annualRate, tenureMonths, moratorium, prepayments, extraEmi }) {
  const P = Math.max(0, Number(principal) || 0);
  const N = Math.max(0, Math.floor(Number(tenureMonths) || 0));
  const rMonthly = (Number(annualRate) || 0) / 12 / 100;
  const morEnabled = moratorium && moratorium.enabled && moratorium.months > 0;
  const morMonths = morEnabled ? Math.floor(moratorium.months) : 0;
  const morType = moratorium?.type || 'full';

  const schedule = [];
  let balance = P;
  let totalInterest = 0;
  let totalPayment = 0;
  let moratoriumInterestCapitalised = 0;
  let moratoriumInterestPaid = 0;

  for (let m = 1; m <= morMonths; m++) {
    const interest = balance * rMonthly;
    if (morType === 'full') {
      balance += interest;
      moratoriumInterestCapitalised += interest;
      schedule.push({ month: m, phase: 'Moratorium', emi: 0, principalPaid: 0, interestPaid: 0, moratoriumInterest: interest, prepayment: 0, balance });
    } else {
      totalInterest += interest;
      totalPayment += interest;
      moratoriumInterestPaid += interest;
      schedule.push({ month: m, phase: 'Moratorium (Int)', emi: interest, principalPaid: 0, interestPaid: interest, prepayment: 0, balance });
    }
  }

  const remaining = Math.max(1, N - morMonths);
  const flatTotalInterest = balance * ((Number(annualRate) || 0) / 100) * (remaining / 12);
  const monthlyPrincipal = balance / remaining;
  const monthlyInterest = flatTotalInterest / remaining;
  const flatEmi = monthlyPrincipal + monthlyInterest;
  const extra = Math.max(0, Number(extraEmi) || 0);

  const prepayMap = new Map();
  prepayments.forEach((p) => {
    if (p && p.month > 0 && p.amount > 0) {
      prepayMap.set(Number(p.month), (prepayMap.get(Number(p.month)) || 0) + Number(p.amount));
    }
  });

  let principalRemaining = balance;
  let month = morMonths;
  const hardCap = morMonths + remaining + 600;
  while (principalRemaining > 0.5 && month < hardCap) {
    month += 1;
    let principalPaid = monthlyPrincipal;
    if (principalPaid > principalRemaining) principalPaid = principalRemaining;
    let extraPay = Math.min(extra, Math.max(0, principalRemaining - principalPaid));
    let interest = monthlyInterest;
    let payment = principalPaid + interest;
    principalRemaining -= principalPaid + extraPay;
    let prepay = prepayMap.get(month) || 0;
    if (prepay > principalRemaining) prepay = principalRemaining;
    principalRemaining -= prepay;
    totalInterest += interest;
    totalPayment += payment + extraPay + prepay;
    schedule.push({
      month,
      phase: 'EMI',
      emi: payment,
      principalPaid,
      interestPaid: interest,
      prepayment: extraPay + prepay,
      balance: Math.max(0, principalRemaining),
    });
    if (principalRemaining <= 0.5) break;
  }

  return {
    schedule,
    summary: {
      emi: Math.round(flatEmi),
      totalInterest: Math.round(totalInterest),
      totalPayment: Math.round(totalPayment),
      monthsTaken: month,
      moratoriumInterestCapitalised: Math.round(moratoriumInterestCapitalised),
      moratoriumInterestPaid: Math.round(moratoriumInterestPaid),
      postMoratoriumPrincipal: Math.round(P + moratoriumInterestCapitalised),
    },
  };
}

// --- Aggregate schedule per year (for bar/line charts) --------------------

export function scheduleByYear(schedule) {
  const map = new Map();
  schedule.forEach((row) => {
    const yr = Math.max(1, Math.ceil(row.month / 12));
    const cur = map.get(yr) || { year: yr, principal: 0, interest: 0, moratoriumInterest: 0, balance: 0 };
    cur.principal += Number(row.principalPaid) || 0;
    cur.interest += Number(row.interestPaid) || 0;
    cur.moratoriumInterest += Number(row.moratoriumInterest) || 0;
    cur.balance = Number(row.balance) || 0;
    map.set(yr, cur);
  });
  return Array.from(map.values()).sort((a, b) => a.year - b.year).map((r) => ({
    ...r,
    principal: Math.round(r.principal),
    interest: Math.round(r.interest),
    moratoriumInterest: Math.round(r.moratoriumInterest),
    balance: Math.round(r.balance),
  }));
}

// --- Formatting -------------------------------------------------------------

export function formatINR(value) {
  if (value == null || isNaN(value)) return '\u20B90';
  const num = Math.round(Number(value));
  return '\u20B9' + num.toLocaleString('en-IN');
}

export function formatINRShort(value) {
  const num = Math.round(Number(value) || 0);
  if (num >= 10000000) return '\u20B9' + (num / 10000000).toFixed(2) + ' Cr';
  if (num >= 100000) return '\u20B9' + (num / 100000).toFixed(2) + ' L';
  if (num >= 1000) return '\u20B9' + (num / 1000).toFixed(1) + ' K';
  return '\u20B9' + num.toLocaleString('en-IN');
}

export function parseINRInput(str) {
  if (typeof str !== 'string') str = String(str ?? '');
  const cleaned = str.replace(/[^\d.]/g, '');
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

export function monthsToLabel(months) {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y && m) return `${y}y ${m}m`;
  if (y) return `${y}y`;
  return `${m}m`;
}
